import { useEffect, useState } from "react";
import {
  Link,
  NavigateFunction,
  useNavigate,
  useParams,
} from "react-router-dom";

import { httpGet } from "../http";
import {
  ArrowLeftIcon,
  MapPinIcon,
  CalendarIcon,
  BuildingIcon,
  DollarSignIcon,
  PackageIcon,
  HashIcon,
  ScaleIcon,
  ReceiptIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "lucide-react";
import { useToast } from "../components/Toast";
import { DateTime } from "luxon";
import {
  CompanyDocument,
  DecimalField,
  MoneyField,
} from "../helpers/br_number_field";

type PurchaseProductType = {
  id: string;
  name: string;
  gtinCode: string;
  ncmCode: string;
  grossValue: string;
  netValue: string;
  quantity: string;
  unity: string;
  unityValue: string;
};

type MetadataType = {
  storeName?: string;
  storeDocument?: string;
  storeAddress?: string;
  storePostalCode?: string;
  date?: string;
  total?: string;
};

export type PurchaseType = {
  id: string;
  status: string;
  taxCouponCode: string;
  createdAt: string;
  metadata: MetadataType;
  PurchaseProducts?: PurchaseProductType[];
};

type StateType = {
  loaded: boolean;
  purchase: PurchaseType | null;
};

type PurchaseProductPropsType = {
  product: PurchaseProductType;
};

const PurchaseProduct = (props: PurchaseProductPropsType) => {
  const p = props.product;
  const code = p.gtinCode || p.ncmCode;
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 bg-blue-100 rounded-lg">
              <PackageIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 mb-1">{p.name}</h4>
              {code && (
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <HashIcon className="h-3 w-3" />
                  <span>{code}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-lg font-bold text-gray-900">
                <MoneyField value={p.netValue} />
              </div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div className="ml-2">
              {isExpanded ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="flex items-center gap-2">
              <DollarSignIcon className="h-4 w-4 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  <MoneyField value={p.unityValue} />
                </div>
                <div className="text-xs text-gray-500">Preço unitário</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ScaleIcon className="h-4 w-4 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  <DecimalField value={p.quantity} /> {p.unity}
                </div>
                <div className="text-xs text-gray-500">Quantidade</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const formatCouponCode = (code: string): string => {
  return code.replace(/(.{4})/g, "$1 ").trim();
};

const loadPurchase = async (
  id: string,
  setPurchase: Function,
  navigate: NavigateFunction,
  showError: (message: string) => void,
) => {
  try {
    const result = await httpGet(`/api/v1/purchases/${id}`);

    if (result.error === "logged_out") {
      navigate("/dash/logout");
    } else if (result.error === "offline") {
      setPurchase(result.body);
    } else if (result.error) {
      showError("Falha ao obter dados da compra");
    } else if (result.body) {
      setPurchase(result.body);
    }
  } finally {
  }
};

export const PurchaseShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [state, setState] = useState<StateType>({
    loaded: false,
    purchase: null,
  });

  useEffect(() => {
    if (id && !state.loaded) {
      const setPurchase = (purchase: PurchaseType | null) => {
        setState((prev) => ({
          ...prev,
          loaded: true,
          purchase,
        }));
      };

      loadPurchase(id, setPurchase, navigate, showError);
    }
  }, [state.loaded, id, navigate, showError]);

  const p = state.purchase;
  const pm = p?.metadata || {};
  let purchaseDateTime;

  if (pm.date) {
    purchaseDateTime = DateTime.fromISO(pm.date || "");
  }

  const purchaseProducts = state.purchase?.PurchaseProducts || [];

  return (
    <div className="container mx-auto px-4 pt-8 pb-24">
      <div className="mb-8">
        <Link
          to={
            state.purchase?.status === "loaded"
              ? "/dash/purchases"
              : "/dash/purchases/pending"
          }
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span className="font-medium">Voltar para Compras</span>
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8 relative">
        {!state.loaded && (
          <div className="absolute inset-0 bg-white bg-opacity-80 rounded-xl flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Carregando compra...</p>
            </div>
          </div>
        )}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3 flex-col lg:flex-row">
            <div className="p-2 bg-blue-600 rounded-lg">
              <ReceiptIcon className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                Detalhes da Compra
              </h1>
              <p className="text-blue-700">
                Informações completas da sua compra
              </p>
            </div>
            {p?.taxCouponCode && (
              <div className="text-center lg:text-right mt-4 lg:mt-0">
                <p className="text-sm text-blue-600 font-medium">
                  Cupom Fiscal
                </p>
                <p className="text-lg font-mono font-bold text-gray-900">
                  {formatCouponCode(p.taxCouponCode)}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              {pm.storeName && pm.storeDocument && (
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BuildingIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                      {pm.storeName}
                    </h2>
                    <div className="text-sm text-gray-600 mb-2">
                      <CompanyDocument value={pm.storeDocument} />
                    </div>
                  </div>
                </div>
              )}

              {(pm.storeAddress || pm.storePostalCode) && (
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MapPinIcon className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Endereço</p>
                    <p className="text-gray-600">
                      {pm.storeAddress}
                      {pm.storeAddress && pm.storePostalCode && " - "}
                      {pm.storePostalCode}
                    </p>
                  </div>
                </div>
              )}

              {purchaseDateTime && (
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Data da Compra</p>
                    <p className="text-gray-600">
                      {purchaseDateTime.toFormat("dd/MM/yyyy")} às{" "}
                      {purchaseDateTime.toFormat("HH:mm")}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {pm.total && (
              <div className="flex items-center justify-center lg:justify-end">
                <div className="bg-green-50 rounded-xl p-6 text-center w-full lg:w-auto">
                  <div className="flex justify-center mb-3">
                    <div className="p-3 bg-green-600 rounded-full">
                      <DollarSignIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm font-medium mb-1">
                    Valor Total
                  </p>
                  <p className="text-3xl font-bold text-green-600">
                    <MoneyField value={pm.total} />
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-8 relative">
        {!state.loaded && (
          <div className="absolute inset-0 bg-white bg-opacity-80 rounded-xl flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Carregando produtos...</p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 mb-6 ml-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Produtos Comprados
            </h2>
            {purchaseProducts.length > 0 && (
              <p className="text-gray-600">
                {purchaseProducts.length}{" "}
                {purchaseProducts.length === 1 ? "item" : "itens"}{" "}
              </p>
            )}
          </div>
        </div>

        {state.purchase?.PurchaseProducts &&
        state.purchase.PurchaseProducts.length > 0 ? (
          <div className="grid gap-4">
            {state.purchase.PurchaseProducts.map((pp) => (
              <PurchaseProduct product={pp} key={pp.id} />
            ))}
          </div>
        ) : state.loaded ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <PackageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum produto encontrado
            </h3>
            <p className="text-gray-600">
              Esta compra ainda não possui produtos cadastrados.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};
